// Claude: add/edit buyer as full page — replaced BuyerFormModal 25-Apr-2026
// Claude: plan dropdown opens renew modal immediately (on selection, not Submit) — matches seller UX 25-Apr-2026
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box, Button, Divider, Flex, Grid, GridItem, Input, Text, Textarea,
} from "@chakra-ui/react";
import { AutoComplete, Modal, Select } from "antd";
import moment from "moment-timezone";
import { MdArrowBack } from "react-icons/md";
import { AdminBuyer } from "../../Interface/AdminInterfaces";
import { AdminAPI } from "../../Apis/adminAPI";
import { AdminBuyerAPI } from "../../Apis/adminBuyerAPI";
import { filterCountries } from "../../Utils/countries";
import { validateEmail } from "../../Utils/Validator";
import { runVerificationCheck } from "../../Utils/verificationCheck";
import { errorHandler } from "../../Utils/handler";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import AdminDigitalAssets from "./AdminDigitalAssets";
import AdminBuyerSubscriptionSection from "./BuyerModals/AdminBuyerSubscriptionSection";
import BuyerRenewModal, { RenewData } from "./BuyerModals/BuyerRenewModal";
import BuyerOverrideModal from "./BuyerModals/BuyerOverrideModal";

const timezones = moment.tz.names();
const mandatoryFields = ["buyerCompanyName", "buyerPersonName", "buyerOfficialEmail"];

const emptyForm: AdminBuyer = {
  eventId: "", buyerCompanyName: "", buyerPersonName: "",
  buyerPersonCity: "", buyerPersonState: "", buyerPersonCountry: "",
  buyerOfficialEmail: "", buyersocialEmail: "", buyerPhoneNumber: "",
  buyerDesignation: "", buyerTimezone: "", buyerCompanyWebsite: "",
  buyerCompanyInfo: "", industryIds: [], subscriptionPlanId: "",
  buyerCompanyType: [], buyerCompanyTypeOther: "", adminNotes: "",
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <GridItem colSpan={3}>
    <Box mt={2} mb={1}>
      <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
        {children}
      </Text>
      <Divider mt={1} borderColor="gray.200" />
    </Box>
  </GridItem>
);

const BuyerEditPage = () => {
  const { buyerId } = useParams<{ buyerId: string }>();
  const isEdit = !!buyerId;
  const location = useLocation();
  const navigate = useNavigate();
  const { error, success } = useToastWrapper();
  const { Option } = Select;

  const searchState = (location.state as any)?.searchState;
  const editInitialPlanId: string = (location.state as any)?.formData?.subscriptionPlanId ?? "";

  const [formData, setFormData] = useState<AdminBuyer>(
    (location.state as any)?.formData ?? { ...emptyForm }
  );
  const [buyerEventIds, setBuyerEventIds] = useState<string[]>([]);
  const [countryOptions, setCountryOptions] = useState<{ value: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const [eventData, setEventData] = useState<any[]>([]);
  const [buyerPlansData, setBuyerPlansData] = useState<any[]>([]);
  const [industryNodes, setTaxonomyNodes] = useState<any[]>([]);

  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [renewBuyerInfo, setRenewBuyerInfo] = useState<any>(null);
  const [renewLoading, setRenewLoading] = useState(false);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);

  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [overrideBuyerId, setOverrideBuyerId] = useState("");
  const [overrideStatus, setOverrideStatus] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideNotes, setOverrideNotes] = useState<any[]>([]);
  const [domainConflictEmail, setDomainConflictEmail] = useState<string | null>(null);
  const [overrideCurrentStatus, setOverrideCurrentStatus] = useState("");
  const [newAdminNote, setNewAdminNote] = useState("");

  const handleChange = (e: any) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  const goBack = () => navigate('/admin/buyers', { state: { restoredSearch: searchState } });

  const validate = () => {
    if (mandatoryFields.some(f => !formData[f as keyof AdminBuyer])) {
      error("Please fill all the mandatory fields", ""); return false;
    }
    if (!validateEmail(formData.buyerOfficialEmail)) { error("Please enter valid email", ""); return false; }
    if (formData.buyersocialEmail && !validateEmail(formData.buyersocialEmail)) { error("Please enter valid email", ""); return false; }
    if (isEdit && !newAdminNote.trim()) { error("Please enter admin notes", ""); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload: any = { ...formData };
    if (!payload.eventId) delete payload.eventId;
    if (isEdit) payload.eventIds = buyerEventIds;
    if (!isEdit) delete payload.subscriptionPlanId; // Claude: subscription not assignable on Add — empty string breaks ObjectId cast 27-04-2026

    if (isEdit) {
      const timestamp = moment().format("DD/MM/YYYY HH:mm");
      payload.adminNotes = `[${timestamp}] ${newAdminNote.trim()}${formData.adminNotes ? `\n\n${formData.adminNotes}` : ""}`;
      setLoading(true);
      try {
        const res = await AdminAPI.putBuyer(buyerId!, payload);
        success("Buyer edited successfully", "");
        if (res?.data?.verificationTriggered) {
          setOverrideBuyerId(res.data.buyerId);
          setOverrideStatus(""); setOverrideReason(""); setOverrideNotes([]);
          setDomainConflictEmail(null); setOverrideCurrentStatus("");
          setIsOverrideModalOpen(true);
        } else {
          goBack();
        }
      } catch (err) { error(errorHandler(err)); }
      finally { setLoading(false); }
    } else {
      const verifResult = runVerificationCheck(formData.buyerOfficialEmail, formData.buyerCompanyWebsite);
      const doCreate = async () => {
        setLoading(true);
        try {
          await AdminAPI.postBuyer(payload);
          success("Buyer added successfully", "");
          goBack();
        } catch (err) { error(errorHandler(err)); }
        finally { setLoading(false); }
      };
      if (verifResult.status === "unverified") {
        Modal.confirm({
          title: "Verification Warning",
          content: `This buyer will be created as unverified. Reason: ${verifResult.reason}. Continue?`,
          okText: "Continue", cancelText: "Cancel", onOk: doCreate,
        });
        return;
      }
      await doCreate();
    }
  };

  const handleOverrideVerification = async () => {
    if (!overrideStatus) { error("Please select a status"); return; }
    if (!overrideReason.trim()) { error("Please provide a reason"); return; }
    setLoading(true);
    try {
      await AdminAPI.overrideBuyerVerification(overrideBuyerId, overrideStatus, overrideReason);
      success("Verification status updated");
      setIsOverrideModalOpen(false);
      goBack();
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  const handleConfirmRenew = async (data: RenewData) => {
    if (!renewBuyerInfo) return;
    setRenewLoading(true);
    try {
      await AdminBuyerAPI.renewBuyerSubscription(
        renewBuyerInfo.buyerId,
        data.subscriptionPlanId ? { subscriptionPlanId: data.subscriptionPlanId } : {},
      );
      if (data.date && data.paymentReference) {
        // Claude: rfxAdded auto-derived from plan.rfxLimit — removed from modal UI 25-Apr-2026
        const plan = buyerPlansData.find((p: any) => p._id === data.subscriptionPlanId);
        await AdminBuyerAPI.addBuyerPaymentRecord(renewBuyerInfo.buyerId, {
          date: data.date, paymentReference: data.paymentReference,
          planName: data.planName ?? "",
          planAmount: parseFloat(data.planAmount ?? "0") || 0,
          creditsUtilized: parseFloat(data.creditsUtilized ?? "0") || 0,
          paidAmount: parseFloat(data.paidAmount ?? "0") || 0,
          rfxAdded: plan?.rfxLimit ?? 0,
        });
      }
      if (data.creditsUtilized && parseFloat(data.creditsUtilized) > 0) {
        await AdminBuyerAPI.adjustBuyerCredits(renewBuyerInfo.buyerId, { amountUsed: parseFloat(data.creditsUtilized) });
      }
      success("Subscription renewed successfully", "");
      setIsRenewModalOpen(false);
      setSubscriptionRefreshKey(k => k + 1);
    } catch (err) { error(errorHandler(err)); }
    finally { setRenewLoading(false); }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [ev, plans, tree] = await Promise.all([
          AdminAPI.getEvent(),
          AdminAPI.getBuyerSubscriptions(),
          AdminAPI.getIndustryTree(),
        ]);
        if (ev?.data?.data?.length > 0)
          setEventData(ev.data.data.map((e: any) => ({ label: e.eventName, value: e._id })));
        setBuyerPlansData(plans?.data?.data?.subscriptions ?? []);
        setTaxonomyNodes(Array.isArray(tree) ? tree : (tree?.data ?? []));
      } catch (err) { error(errorHandler(err)); }
    };
    const loadEditData = async () => {
      if (!buyerId) return;
      try {
        const evIds = await AdminAPI.getBuyerEventIds(buyerId);
        setBuyerEventIds(evIds?.data?.data ?? []);
      } catch (err) { console.log(err); }
    };
    loadData();
    if (isEdit) loadEditData();
  }, []);

  // Claude: status reflects whether a plan is assigned — not whether it changed in the current session 26-04-2026
  const subscriptionStatus = formData.subscriptionPlanId ? "Active" : "Inactive";
  const statusColor = subscriptionStatus === "Active" ? "green.600" : "red.400";

  return (
    <div className="p-4">
      <BuyerRenewModal
        isOpen={isRenewModalOpen}
        onClose={() => {
          setIsRenewModalOpen(false);
          setFormData(p => ({ ...p, subscriptionPlanId: editInitialPlanId }));
        }}
        buyer={renewBuyerInfo}
        plansData={buyerPlansData}
        onConfirm={handleConfirmRenew}
        loading={renewLoading}
      />

      <BuyerOverrideModal
        isOpen={isOverrideModalOpen}
        onClose={() => { setIsOverrideModalOpen(false); goBack(); }}
        overrideNotes={overrideNotes}
        overrideBuyerId={overrideBuyerId}
        overrideStatus={overrideStatus}
        setOverrideStatus={setOverrideStatus}
        overrideReason={overrideReason}
        setOverrideReason={setOverrideReason}
        domainConflictEmail={domainConflictEmail}
        setDomainConflictEmail={setDomainConflictEmail}
        overrideCurrentStatus={overrideCurrentStatus}
        onConfirm={handleOverrideVerification}
      />

      <Flex mb={4} alignItems="center" gap={3}>
        <Button size="sm" h="28px" fontSize="xs" variant="outline" borderColor="#0E122F" color="#0E122F"
          leftIcon={<MdArrowBack />} onClick={goBack}
        >
          Back
        </Button>
        <Text fontSize="md" fontWeight={600}>
          {isEdit ? `Edit Buyer${formData.buyerCompanyName ? ` — ${formData.buyerCompanyName}` : ""}` : "Add Buyer"}
        </Text>
      </Flex>

      <Box padding={2} bg="white" borderRadius="md" border="1px solid #eee">
        <Grid gap={2} templateColumns="repeat(3, 1fr)">

          {/* Claude: Company section reordered — Name, Industry, Website, Type, Type Other, Events 25-Apr-2026 */}
          <SectionLabel>Company</SectionLabel>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company Name <span style={{ color: "red" }}>*</span></Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Company Name"
                name="buyerCompanyName" value={formData.buyerCompanyName} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Industry</Text>
              <Select
                mode="multiple" showSearch optionFilterProp="label" style={{ width: "100%" }}
                placeholder="Select industries" value={formData.industryIds}
                onChange={(values) => setFormData({ ...formData, industryIds: values })}
                options={industryNodes.filter((n: any) => !n.parentId).map((n: any) => ({ label: n.name, value: n._id }))}
                allowClear
              />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company Website</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Company Website"
                name="buyerCompanyWebsite" value={formData.buyerCompanyWebsite} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Type</Text>
              <Select
                mode="multiple" placeholder="Select company type" style={{ width: "100%" }}
                value={formData.buyerCompanyType}
                onChange={(values) => setFormData({ ...formData, buyerCompanyType: values })}
                options={[
                  { label: "Manufacturing", value: "Manufacturing" },
                  { label: "Trade", value: "Trade" },
                  { label: "Services / Solutions", value: "Services/Solutions" },
                  { label: "Others", value: "Others" },
                ]}
                allowClear
              />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Type Other</Text>
              <Input height="28px" fontSize="xs" placeholder="If Others selected"
                name="buyerCompanyTypeOther" value={formData.buyerCompanyTypeOther ?? ""}
                onChange={handleChange}
                disabled={!formData.buyerCompanyType?.includes("Others")} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Event Name{isEdit ? "s" : ""}</Text>
              {isEdit ? (
                <Select
                  mode="multiple" showSearch optionFilterProp="label"
                  placeholder="Select Events" style={{ width: "100%", minHeight: "28px" }}
                  value={buyerEventIds.length ? buyerEventIds : undefined}
                  options={eventData}
                  onChange={(values: string[]) => setBuyerEventIds(values.filter(Boolean))}
                />
              ) : (
                <Select
                  showSearch optionFilterProp="label" placeholder="Select Event"
                  style={{ height: "28px" }} options={eventData}
                  value={formData.eventId}
                  onChange={(value) => setFormData({ ...formData, eventId: value })}
                />
              )}
            </Box>
          </GridItem>

          <GridItem colSpan={3}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company Info</Text>
              <Textarea fontSize="xs" placeholder="Enter Company Info"
                name="buyerCompanyInfo" value={formData.buyerCompanyInfo} onChange={handleChange} rows={3} resize="none" />
            </Box>
          </GridItem>

          <SectionLabel>Contact Person</SectionLabel>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Person Name <span style={{ color: "red" }}>*</span></Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Person Name"
                name="buyerPersonName" value={formData.buyerPersonName} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Official Email <span style={{ color: "red" }}>*</span></Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Official Email"
                name="buyerOfficialEmail" value={formData.buyerOfficialEmail} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Phone</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Phone"
                name="buyerPhoneNumber" value={formData.buyerPhoneNumber} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Designation</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Designation"
                name="buyerDesignation" value={formData.buyerDesignation} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Social Email</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Social Email"
                name="buyersocialEmail" value={formData.buyersocialEmail} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Country</Text>
              <AutoComplete
                style={{ width: "100%", height: "28px" }}
                placeholder="Enter Country"
                value={formData.buyerPersonCountry}
                options={countryOptions}
                onSearch={(q) => setCountryOptions(filterCountries(q))}
                onChange={(val) => setFormData(prev => ({ ...prev, buyerPersonCountry: val ?? "" }))}
              />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">State</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter State"
                name="buyerPersonState" value={formData.buyerPersonState} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">City</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter City"
                name="buyerPersonCity" value={formData.buyerPersonCity} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Timezone</Text>
              <Select
                showSearch placeholder="Select Timezone" optionFilterProp="children"
                value={formData.buyerTimezone}
                onChange={(value) => setFormData({ ...formData, buyerTimezone: value })}
                style={{ width: "100%", height: "28px" }}
              >
                {timezones.map((tz) => (<Option key={tz} value={tz}>{tz}</Option>))}
              </Select>
            </Box>
          </GridItem>

          {/* Claude: Subscription Plan + Status moved inside Subscription section; Assignment section removed 25-Apr-2026 */}
          {isEdit && buyerId && (
            <>
              <SectionLabel>Subscription</SectionLabel>
              <GridItem colSpan={1}>
                <Box display="flex" flexDirection="column" gap="4px">
                  <Text fontSize="xs" fontWeight="500">Subscription Plan</Text>
                  <Select
                    showSearch optionFilterProp="label" placeholder="Assign Subscription Plan"
                    style={{ width: "100%", height: "28px" }} allowClear
                    value={formData.subscriptionPlanId || undefined}
                    options={buyerPlansData.map((p: any) => ({ label: p.planName, value: p._id }))}
                    onChange={(value) => {
                      setFormData(p => ({ ...p, subscriptionPlanId: value ?? "" }));
                      if (value) {
                        setRenewBuyerInfo({ buyerId, subscriptionPlanId: value });
                        setIsRenewModalOpen(true);
                      }
                    }}
                  />
                </Box>
              </GridItem>
              <GridItem colSpan={1}>
                <Box display="flex" flexDirection="column" gap="4px">
                  <Text fontSize="xs" fontWeight="500">Subscription Status</Text>
                  <Text fontSize="xs" fontWeight={600} color={statusColor} mt="6px">{subscriptionStatus}</Text>
                </Box>
              </GridItem>
              <GridItem colSpan={1} />
              <GridItem colSpan={3}>
                <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
                  <AdminBuyerSubscriptionSection key={subscriptionRefreshKey} buyerId={buyerId} selectedPlanId={formData.subscriptionPlanId} plansData={buyerPlansData} />
                </Box>
              </GridItem>
            </>
          )}

          {isEdit && buyerId && (
            <>
              <SectionLabel>Digital Assets</SectionLabel>
              <GridItem colSpan={3}>
                <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
                  <AdminDigitalAssets buyerId={buyerId} />
                </Box>
              </GridItem>
            </>
          )}

        </Grid>

        {/* Claude: admin notes redesign — append-only log; new note mandatory on edit 27-04-2026 */}
        <Box mt={4}>
          <Box mb={1}>
            <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
              Admin Notes
            </Text>
            <Divider mt={1} borderColor="gray.200" />
          </Box>
          {isEdit ? (
            <>
              <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={2} mb={2} maxH="120px" overflowY="auto">
                {formData.adminNotes ? (
                  <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap">{formData.adminNotes}</Text>
                ) : (
                  <Text fontSize="xs" color="gray.400" fontStyle="italic">No previous notes.</Text>
                )}
              </Box>
              <Text fontSize="xs" fontWeight="500" mb={1}>New Note <span style={{ color: "red" }}>*</span></Text>
              <Textarea fontSize="xs" placeholder="Add note (required) — not visible to buyer"
                value={newAdminNote} onChange={(e) => setNewAdminNote(e.target.value)} rows={3} resize="none" />
            </>
          ) : (
            <Textarea fontSize="xs" placeholder="Internal notes (not visible to buyer)"
              name="adminNotes" value={formData.adminNotes ?? ""} onChange={handleChange} rows={3} resize="none" />
          )}
        </Box>

        <Box mt={4}>
          <Flex justifyContent="end" gap={2}>
            <Button h="28px" fontSize="xs" variant="outline" onClick={goBack}>Cancel</Button>
            <Button h="28px" fontSize="xs" background="#0E122F" color="white" px={3}
              _hover={{ background: "#2E7D32" }} isLoading={loading} onClick={handleSubmit}
            >
              Submit
            </Button>
          </Flex>
        </Box>
      </Box>
    </div>
  );
};

export default BuyerEditPage;
